import { Footer } from '@workspace/shared/components/Footer';
import { Header } from '@workspace/shared/components/Header';
import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { type ReactElement, type Ref, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { Link, useLocation } from 'react-router-dom';

import AppIcon from '~/assets/img/appIcon.png';
import { company } from '~/company/Company';
import FooterSaas from '~/components/Footer/Footer';
import { FooterMini } from '~/components/FooterMini/FooterMini';
import Icon from '~/components/Icon/Icon';
import PageHeader from '~/components/isolated/PageHeader';
import { EmptyHeader, MobileHeaderCD } from '~/components/MobileHeaderCD';
import MobileHeaderMarketplace from '~/components/MobileHeaderMarketplace/MobileHeaderMarketplace';
import { HeaderAddressButton } from '~/components/mcosmetic/header/HeaderAddressButton';
import { HeaderNavigation } from '~/components/mcosmetic/header/HeaderNavigation';
import { useGlobal } from '~/hooks/useGlobal';
import { useHeaderHeight } from '~/hooks/useHeaderHeight';
import { MODAL_EDIT_ADDRESS_PARAM, useModal } from '~/hooks/useModal';
import { mainStore, userStore } from '~/stores';
import { CatalogCategory } from '~/stores/CategoriesStore';
import { getAddressModalParams } from '~/utils/addressModal';
import { isIOS } from '~/utils/platform';

import { BottomNavigationBar } from './BottomNavigationBar';
import BottomNavigationBarV2 from './BottomNavigationBar/BottomNavigationBarV2';
import { ROUTES } from './constants';
import styles from './MobileLayout.module.scss';
import { getPageHeaderConfig } from './pageHeaderConfig';

interface MobileLayoutProps {
  children: ReactElement;
  layoutRef: Ref<HTMLDivElement>;
}

export const MobileDownloadHeader = () => {
  const [visible, setVisible] = useState(true);
  const { t } = useTranslation();

  const handleCloseClick = () => {
    setVisible(false);
  };

  const handleDownloadClick = () => {
    setVisible(false);
    mainStore.setIsAppInstalled(true);
  };

  if (!visible) {
    return null;
  }

  return (
    <div className={styles.mobileHeaderContainer}>
      <div className={styles.mobileHeaderContentContainer}>
        <div onClick={handleCloseClick}>
          <Icon type="close" />
        </div>
        <img className={styles.mobileHeaderIcon} src={AppIcon} alt="" />
        <div className={styles.descriptionContainer}>{t('downloadApp')}</div>
      </div>

      <a
        href={
          isIOS()
            ? company.config.appStoreUrl.ios
            : company.config.appStoreUrl.android
        }
        target="_blank"
        className="button _primary product-card__buy small"
        rel="noreferrer"
        onClick={handleDownloadClick}
      >
        {mainStore.isAppInstalled
          ? t('phrases:openApp')
          : t('phrases:download')}
      </a>
    </div>
  );
};

const MobileLayout = observer(({ children, layoutRef }: MobileLayoutProps) => {
  const { isMobile } = useGlobal();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const { openModal } = useModal();

  const headerRef = useRef<HTMLHeadingElement>(null);

  const isProductPage = /^\/p\/(.*?)$/gi.test(pathname);
  const isSearchPage = pathname === '/search';
  const isHomePage = pathname === '/';
  const isProfilePage = pathname === '/cabinet';
  const shouldHideMarketplaceShopHeader =
    company.isMarketplaceVariant && pathname.startsWith('/shop');

  const isHeaderHidden = useMemo(
    () =>
      !company.newDesignSystemEnabled ||
      (company.newDesignSystemEnabled && isProductPage) ||
      (company.newDesignSystemEnabled &&
        company.is('mcosmetic') &&
        isProfilePage) ||
      shouldHideMarketplaceShopHeader,
    [
      isProductPage,
      isProfilePage,
      company.newDesignSystemEnabled,
      shouldHideMarketplaceShopHeader,
    ],
  );

  const handleDeliveryAddress = () => {
    const [targetModal, source, params] = getAddressModalParams('header', {
      [MODAL_EDIT_ADDRESS_PARAM]: userStore?.deliveryAddress?.addressId || '',
    });

    openModal(targetModal, source, params);
  };

  const header = (() => {
    const pageHeaderConfig = getPageHeaderConfig(pathname, true, t);
    if (company.newDesignSystemEnabled && pageHeaderConfig) {
      if (shouldHideMarketplaceShopHeader) {
        return null;
      }
      if (pageHeaderConfig.hideOnMobile) {
        return <EmptyHeader ref={headerRef} />;
      }

      if (pageHeaderConfig.useCategoryName) {
        const categorySlug = pathname.split('/c/')[1]?.split('/')[0];
        if (categorySlug) {
          const category = CatalogCategory.getInstance(categorySlug);
          const categoryName = category.data?.name ?? (
            <Skeleton containerClassName={styles.categoryNameSkeleton} />
          );

          return (
            <PageHeader
              ref={headerRef}
              title={categoryName}
              backgroundImage={category.data?.previewImage}
            />
          );
        }
      }

      if (isHeaderHidden) return null;

      return <PageHeader ref={headerRef} title={pageHeaderConfig.title} />;
    }

    if (isHeaderHidden) {
      return null;
    }

    if (company.newDesignSystemEnabled && isSearchPage) {
      return <EmptyHeader ref={headerRef} />;
    }
    if (company.isMarketplaceVariant) {
      if (shouldHideMarketplaceShopHeader) {
        return null;
      }
      return <MobileHeaderMarketplace ref={headerRef} />;
    }

    return company.match({
      mcosmetic: (
        <Header.MCosmetic
          logoWrapper={({ children }) => <Link to="/">{children}</Link>}
          logo={{
            mobile: company.images.logoMobile?.vector,
            desktop: company.images.logo.vector,
          }}
          ref={headerRef}
          shouldChangeColorOnScroll={isHomePage}
        >
          <Header.Address>
            <HeaderAddressButton onClick={handleDeliveryAddress} />
          </Header.Address>
          <Header.Navigation>
            <HeaderNavigation />
          </Header.Navigation>
        </Header.MCosmetic>
      ),
      default: <MobileHeaderCD ref={headerRef} />,
    });
  })();

  const footer = (() => {
    const footerComp = company.match({
      mcosmetic: (
        <Footer.MCosmetic
          logo={{ desktop: company.images.logo.vector }}
          name={company.config.name}
        />
      ),
      default: <FooterSaas />,
    });

    if (!company.hasNewCheckoutElemtnts) {
      return footerComp;
    }

    switch (pathname) {
      case ROUTES.checkout:
        return <FooterMini />;
      default:
        return footerComp;
    }
  })();

  useHeaderHeight(headerRef, [pathname], isHeaderHidden);

  return (
    <>
      <div
        className={classes([
          styles.mobileLayout,
          company.newDesignSystemEnabled && styles.redesign,
          isHeaderHidden && styles.sharp,
        ])}
        ref={layoutRef}
      >
        {isMobile &&
          !mainStore.isRN &&
          company.showMobileDowloadPanel &&
          !company.newDesignSystemEnabled && <MobileDownloadHeader />}
        {header}
        {company.newDesignSystemEnabled ? (
          <div className={styles.content}>{children}</div>
        ) : (
          children
        )}
        {!mainStore.isRN && footer}
      </div>
      {pathname === '/checkout' ? (
        company.newDesignSystemEnabled ? null : (
          <BottomNavigationBar />
        )
      ) : company.newDesignSystemEnabled ? (
        <BottomNavigationBarV2 />
      ) : (
        <BottomNavigationBar />
      )}
    </>
  );
});

export default MobileLayout;
